import { FC, useEffect, useState } from "react";
import { v4 as uuid } from "uuid";
import { observer } from "mobx-react";
import { SelectChangeEvent } from "@mui/material";

import CoreInput from "../../core/CoreInput";
import { useAppStore } from "../../../../hooks/useAppStore";
import CoreTextarea from "../../core/CoreTextarea";
import {
  CONTACT_SUPPORT_ATTACHMENT_LIMITS,
  CONTACT_SUPPORT_PURPOSE_TYPES,
  CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH,
  ContactSupportFormType,
  DENIAL_TYPE_OPTIONS,
} from "../../../../config/constants";
import CoreSelect from "../../core/CoreSelect";
import ContactSupportDropzone from "./ContactSupportDropzone";

interface ContactSupportFormContentProps {
  purpose: string;
  setPurpose: (purpose: string) => void;
  formData: ContactSupportFormType;
  setFormData: (formData: ContactSupportFormType) => void;
  isDisabled: boolean;
}

const PURPOSE_OPTIONS = Object.values(CONTACT_SUPPORT_PURPOSE_TYPES).map(
  (value) => ({
    value,
    label: value,
  }),
);

const { maxFiles, maxFileSizeMb, acceptLabel, allowedExtensions } =
  CONTACT_SUPPORT_ATTACHMENT_LIMITS;

const getFileExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf(".");
  return lastDot === -1 ? "" : fileName.slice(lastDot).toLowerCase();
};

const ContactSupportFormContent: FC<ContactSupportFormContentProps> = observer(
  ({ purpose, setPurpose, formData, setFormData, isDisabled }) => {
    const [attachmentError, setAttachmentError] = useState<string | null>(null);

    const {
      contactSupportUploadFiles: uploadedFiles,
      setContactSupportUploadFiles: setUploadedFiles,
    } = useAppStore();

    // Auto-dismiss the rejection message so it doesn't linger indefinitely.
    useEffect(() => {
      if (!attachmentError) return;
      const timer = setTimeout(() => setAttachmentError(null), 5000);
      return () => clearTimeout(timer);
    }, [attachmentError]);

    const onPurposeChange = (event: SelectChangeEvent<unknown>) => {
      setPurpose(event.target.value as string);
      // Also clear the form data for the new purpose
      setFormData({
        ...formData,
        surgeryDate: "",
        claimNumber: "",
        denialType: "",
      });
    };

    const onFilesSelected = (incomingFiles: File[]) => {
      const remainingSlots = maxFiles - uploadedFiles.length;
      if (remainingSlots <= 0) return;

      const accepted: File[] = [];
      let rejection: { name: string; reason: "type" | "size" } | null = null;

      for (const file of incomingFiles) {
        if (accepted.length >= remainingSlots) break;

        if (!allowedExtensions.includes(getFileExtension(file.name))) {
          rejection = rejection ?? { name: file.name, reason: "type" };
          continue;
        }
        if (file.size > maxFileSizeMb * 1024 * 1024) {
          rejection = rejection ?? { name: file.name, reason: "size" };
          continue;
        }
        accepted.push(file);
      }

      if (accepted.length > 0) {
        setUploadedFiles([
          ...uploadedFiles,
          ...accepted.map((file) => ({
            file,
            status: "attached" as const,
            id: uuid(),
          })),
        ]);
      }

      setAttachmentError(
        rejection
          ? rejection.reason === "type"
            ? `"${rejection.name}" isn't supported. Use ${acceptLabel}`
            : `"${rejection.name}" is larger than ${maxFileSizeMb} MB`
          : null,
      );
    };

    const updateForm = (field: keyof ContactSupportFormType, value: string) => {
      setFormData({
        ...formData,
        [field]: value,
      });
    };

    return (
      <>
        <CoreSelect
          disabled={isDisabled}
          label="Purpose"
          placeholder="Select purpose"
          value={purpose}
          options={PURPOSE_OPTIONS}
          onChange={onPurposeChange}
        />

        {!purpose ? null : purpose === CONTACT_SUPPORT_PURPOSE_TYPES.SUPPORT ? (
          <>
            <CoreInput
              disabled={isDisabled}
              label="Subject"
              value={formData.subject}
              maxChars={CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.subject}
              onChange={(e) => updateForm("subject", e.target.value)}
            />

            <CoreTextarea
              title="Message"
              placeholder="Enter your message"
              disabled={isDisabled}
              value={formData.comment}
              minRows={4}
              maxRows={6}
              disabledSx={{ backgroundColor: "white" }}
              onChange={(e) => updateForm("comment", e.target.value)}
              style={{ resize: "none" }}
              maxChars={CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.comment}
            />
          </>
        ) : (
          <>
            <CoreInput
              disabled={isDisabled}
              label="Patient Name"
              value={formData.patientName}
              onChange={(e) => updateForm("patientName", e.target.value)}
              maxChars={CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.patientName}
            />
            <CoreInput
              disabled={isDisabled}
              label="MRN"
              value={formData.mrn}
              onChange={(e) => updateForm("mrn", e.target.value)}
              maxChars={CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.mrn}
            />
          </>
        )}

        {purpose === CONTACT_SUPPORT_PURPOSE_TYPES.PRESERVICE_REVIEW ? (
          <CoreInput
            disabled={isDisabled}
            label="Surgery Date"
            value={formData.surgeryDate}
            type="date"
            onChange={(e) => updateForm("surgeryDate", e.target.value)}
          />
        ) : null}

        {purpose === CONTACT_SUPPORT_PURPOSE_TYPES.DENIAL_MANAGEMENT ? (
          <>
            <CoreInput
              disabled={isDisabled}
              label="Claim #"
              value={formData.claimNumber}
              onChange={(e) => updateForm("claimNumber", e.target.value)}
              maxChars={CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.claimNumber}
            />
            <CoreSelect
              disabled={isDisabled}
              label="Denial Type"
              value={formData.denialType}
              onChange={(e: SelectChangeEvent<unknown>) =>
                updateForm("denialType", e.target.value as string)
              }
              options={DENIAL_TYPE_OPTIONS}
            />
          </>
        ) : null}

        {purpose ? (
          <>
            {purpose !== CONTACT_SUPPORT_PURPOSE_TYPES.SUPPORT ? (
              <CoreTextarea
                title="Message"
                placeholder="Enter your message"
                disabled={isDisabled}
                value={formData.comment}
                minRows={4}
                maxRows={6}
                disabledSx={{ backgroundColor: "white" }}
                onChange={(e) => updateForm("comment", e.target.value)}
                style={{ resize: "none" }}
                maxChars={CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.comment}
              />
            ) : null}

            <ContactSupportDropzone
              fileCount={uploadedFiles.length}
              disabled={isDisabled}
              errorMessage={attachmentError}
              onFilesSelected={onFilesSelected}
            />
          </>
        ) : null}
      </>
    );
  },
);

export default ContactSupportFormContent;
